/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
import { supabase } from "@/integrations/supabase/client";
import type { Json, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ContentBrief, BriefReferenceInput } from "../types";
import { getFriendlyMessage, normalizeText } from "../_shared";
import { createBriefReferences } from "./references";

export const createBrief = async (
  workspaceId: string,
  values: {
    title: string;
    prompt?: string;
    contentType?: string;
    notes?: string;
    templateId?: string | null;
    templateSource?: string | null;
    wizardAnswers?: Record<string, unknown> | null;
  },
  references: BriefReferenceInput[] = [],
) => {
  const payload: TablesInsert<"content_pieces"> = {
    workspace_id: workspaceId,
    title: normalizeText(values.title),
    content_type: values.contentType?.trim() ?? null,
    template_id: values.templateId ?? null,
    template_source: values.templateSource ?? null,
    wizard_answers: (values.wizardAnswers ?? {}) as never,
  };

  const { data, error } = await supabase.from("content_pieces").insert(payload).select("*").single();
  if (error) throw new Error(getFriendlyMessage(error, "Failed to create the brief"));

  const promptVal = values.prompt?.trim() ?? null;
  const notesVal = values.notes?.trim() ?? null;
  if (promptVal || notesVal) {
    await supabase.rpc("set_content_brief_text", {
      _brief_id: data.id,
      _notes: notesVal,
      _prompt: promptVal,
    });
  }

  if (references.length) {
    await createBriefReferences(workspaceId, data.id, references);
  }

  return data as ContentBrief;
};

export type BriefUpdate = Partial<{
  status: TablesUpdate<"content_pieces">["status"];
  approved_at: TablesUpdate<"content_pieces">["approved_at"];
  notes: string | null;
  prompt: string | null;
}>;

export const updateBrief = async (
  workspaceId: string,
  briefId: string,
  values: BriefUpdate,
) => {
  const payload: TablesUpdate<"content_pieces"> = {
    ...(values.status !== undefined ? { status: values.status } : {}),
    ...(values.approved_at !== undefined ? { approved_at: values.approved_at } : {}),
  };

  if (values.notes !== undefined || values.prompt !== undefined) {
    const { data: existing } = await supabase.rpc("get_decrypted_content_briefs", {
      _workspace_id: workspaceId,
      _ids: [briefId],
    });
    const current = ((existing ?? []) as Array<{ id: string; notes: string | null; prompt: string | null }>)[0];
    const nextNotes = values.notes !== undefined ? (values.notes?.trim() ?? null) : (current?.notes ?? null);
    const nextPrompt = values.prompt !== undefined ? (values.prompt?.trim() ?? null) : (current?.prompt ?? null);
    await supabase.rpc("set_content_brief_text", {
      _brief_id: briefId,
      _notes: nextNotes,
      _prompt: nextPrompt,
    });
  }

  if (Object.keys(payload).length > 0) {
    const { error } = await supabase
      .from("content_pieces")
      .update(payload)
      .eq("workspace_id", workspaceId)
      .eq("id", briefId);
    if (error) throw new Error(getFriendlyMessage(error, "Failed to update the brief"));
  }

  const { data, error: fetchError } = await supabase
    .from("content_pieces")
    .select("*")
    .eq("workspace_id", workspaceId)
    .eq("id", briefId)
    .single();
  if (fetchError) throw new Error(getFriendlyMessage(fetchError, "Failed to load the updated brief"));

  return data as ContentBrief;
};

export const getBriefSelectedAvatarId = (brief: { metadata?: Json | null } | null | undefined): string | null => {
  if (!brief?.metadata || typeof brief.metadata !== "object" || Array.isArray(brief.metadata)) return null;
  const value = (brief.metadata as Record<string, unknown>).selected_avatar_id;
  return typeof value === "string" && value.length > 0 ? value : null;
};

const loadBriefMetadata = async (workspaceId: string, briefId: string): Promise<Record<string, Json | null>> => {
  const { data: existing, error: fetchError } = await supabase
    .from("content_pieces")
    .select("metadata")
    .eq("workspace_id", workspaceId)
    .eq("id", briefId)
    .single();
  if (fetchError) throw new Error(getFriendlyMessage(fetchError, "Failed to load brief metadata"));
  return existing.metadata && typeof existing.metadata === "object" && !Array.isArray(existing.metadata)
    ? (existing.metadata as Record<string, Json | null>)
    : {};
};

const mergePersistedCorpusIds = async (
  workspaceId: string,
  pieceId: string,
  approvedBrief: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
  const existingIds = Array.isArray(approvedBrief.corpus_document_ids)
    ? approvedBrief.corpus_document_ids.filter((v): v is string => typeof v === "string" && v.length > 0)
    : [];

  const { data } = await supabase
    .from("content_brief_plans")
    .select("intake_state")
    .eq("workspace_id", workspaceId)
    .eq("brief_id", pieceId)
    .eq("status", "succeeded")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  const persistedApproved = (data?.intake_state && typeof data.intake_state === "object" && !Array.isArray(data.intake_state))
    ? (data.intake_state as Record<string, unknown>).approved_brief
    : null;
  const persistedIds = persistedApproved && typeof persistedApproved === "object" && !Array.isArray(persistedApproved)
    ? (persistedApproved as Record<string, unknown>).corpus_document_ids
    : null;
  const merged = Array.from(new Set([
    ...existingIds,
    ...(Array.isArray(persistedIds) ? persistedIds.filter((v): v is string => typeof v === "string" && v.length > 0) : []),
  ]));

  return merged.length > 0 ? { ...approvedBrief, corpus_document_ids: merged } : approvedBrief;
};

const writeBriefMetadata = async (
  workspaceId: string,
  briefId: string,
  metadata: Record<string, Json | null>,
  failureMessage: string,
) => {
  const { error } = await supabase
    .from("content_pieces")
    .update({ metadata: metadata as Json })
    .eq("workspace_id", workspaceId)
    .eq("id", briefId);
  if (error) throw new Error(getFriendlyMessage(error, failureMessage));
};

const syncApprovedPlanSnapshots = async (
  workspaceId: string,
  pieceId: string,
  pieceUpdatedAt: string | null,
) => {
  if (!pieceUpdatedAt) return;
  const { error } = await supabase
    .from("content_brief_plans")
    .update({ brief_updated_at_snapshot: pieceUpdatedAt })
    .eq("workspace_id", workspaceId)
    .eq("brief_id", pieceId)
    .eq("status", "succeeded");
  if (error) throw new Error(getFriendlyMessage(error, "Failed to sync the approved planning pass"));
};

export const setBriefSelectedAvatar = async (
  workspaceId: string,
  briefId: string,
  avatarId: string | null,
) => {
  const current = await loadBriefMetadata(workspaceId, briefId);
  await writeBriefMetadata(
    workspaceId,
    briefId,
    { ...current, selected_avatar_id: avatarId },
    "Failed to save avatar selection",
  );
};

export const setBriefContextOverrides = async (
  workspaceId: string,
  briefId: string,
  overrides: string[],
) => {
  const current = await loadBriefMetadata(workspaceId, briefId);
  await writeBriefMetadata(
    workspaceId,
    briefId,
    { ...current, context_overrides: overrides as unknown as Json },
    "Failed to save context overrides",
  );
};

/**
 * Per-brief citation render mode (footnotes vs endnotes). Only flips where
 * the in-text citations are *placed* — the underlying references and the
 * `cited` flag are unchanged. Stored in `content_briefs.metadata.citationMode`.
 */
export type BriefCitationMode = "footnotes" | "endnotes";

export const setBriefCitationMode = async (
  workspaceId: string,
  briefId: string,
  mode: BriefCitationMode,
) => {
  const current = await loadBriefMetadata(workspaceId, briefId);
  await writeBriefMetadata(
    workspaceId,
    briefId,
    { ...current, citationMode: mode },
    "Failed to save citation mode",
  );
};

/**
 * Per-piece citation-style override. Stored in
 * `content_pieces.metadata.citationStyle`. `null` clears the override so the
 * workspace default applies again. Validation of the style string itself
 * happens at the call site (UI restricts to CITATION_STYLES); we accept any
 * string here so the storage layer stays decoupled from `citationStyles.ts`.
 */
export const setBriefCitationStyle = async (
  workspaceId: string,
  briefId: string,
  style: string | null,
) => {
  const current = await loadBriefMetadata(workspaceId, briefId);
  await writeBriefMetadata(
    workspaceId,
    briefId,
    { ...current, citationStyle: style },
    "Failed to save citation style override",
  );
};

/** Reads `metadata.citationStyle` if present. Returns null otherwise. */
export const readBriefCitationStyleOverride = (
  metadata: Json | null | undefined,
): string | null => {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const raw = (metadata as Record<string, unknown>).citationStyle;
  return typeof raw === "string" && raw.length > 0 ? raw : null;
};

/** Clones a brief's prompt, content_type, notes, destination_defaults,
 *  metadata, and all linked references into a new draft-status brief.
 *  Returns the new brief id. */
export const duplicateBrief = async (workspaceId: string, briefId: string): Promise<string> => {
  const { data: source, error: sourceError } = await supabase
    .from("content_pieces")
    .select("id, workspace_id, title, content_type, status, destination_defaults, metadata")
    .eq("workspace_id", workspaceId)
    .eq("id", briefId)
    .single();
  if (sourceError) throw new Error(getFriendlyMessage(sourceError, "Failed to load the brief to duplicate"));

  const { data: decryptedRows, error: decryptError } = await (supabase as any).rpc("get_decrypted_content_briefs", {
    _workspace_id: workspaceId,
    _ids: [briefId],
  });
  if (decryptError) throw new Error(getFriendlyMessage(decryptError, "Failed to load brief contents to duplicate"));
  const decrypted = ((decryptedRows ?? []) as Array<{ id: string; notes: string | null; prompt: string | null }>)[0] ?? { notes: null, prompt: null };

  const sourceBrief = source as ContentBrief;
  const { data: refs, error: refsError } = await supabase
    .from("content_piece_references")
    .select("reference_type, reference_id, label_snapshot, metadata, sort_order")
    .eq("workspace_id", workspaceId)
    .eq("piece_id", briefId)
    .order("sort_order", { ascending: true });
  if (refsError) throw new Error(getFriendlyMessage(refsError, "Failed to load the brief's references"));

  const insertPayload: TablesInsert<"content_pieces"> = {
    workspace_id: workspaceId,
    title: `${sourceBrief.title} (copy)`,
    content_type: sourceBrief.content_type,
    destination_defaults: sourceBrief.destination_defaults,
    metadata: sourceBrief.metadata,
    status: "draft",
  };

  const { data: created, error: insertError } = await supabase
    .from("content_pieces")
    .insert(insertPayload)
    .select("id")
    .single();
  if (insertError) throw new Error(getFriendlyMessage(insertError, "Failed to duplicate the brief"));

  const newId = (created as { id: string }).id;

  await (supabase as any).rpc("set_content_brief_text", {
    _piece_id: newId,
    _notes: decrypted.notes,
    _prompt: decrypted.prompt,
  });

  if (refs && refs.length) {
    const refPayload = refs.map((row, index) => ({
      workspace_id: workspaceId,
      piece_id: newId,
      reference_type: row.reference_type,
      reference_id: row.reference_id,
      label_snapshot: row.label_snapshot,
      metadata: (row.metadata ?? {}) as Json,
      sort_order: row.sort_order ?? index,
    })) satisfies TablesInsert<"content_piece_references">[];
    const { error: refInsertError } = await supabase.from("content_piece_references").insert(refPayload);
    if (refInsertError) {
      throw new Error(getFriendlyMessage(refInsertError, "Brief copied but references failed to attach"));
    }
  }

  return newId;
};

/**
 * Approve the planning agent's proposed brief for this piece. In a SINGLE
 * update, syncs the three fields the Writing Agent depends on so its corpus
 * retrieval (starred style samples filter) and avatar blending actually run:
 *   - `approved_brief_json` — full spec
 *   - `content_type` <- approvedBrief.publish_type_key (REQUIRED)
 *   - `metadata.selected_avatar_id` <- approvedBrief.audience_avatar_id
 *
 * Refuses to approve if `publish_type_key` is null/empty — the planning agent
 * must resolve a publish type before the Writing Agent can filter for the
 * user's starred style samples of that type. See
 * mem://features/planning-agent.md (Phase B2) and
 * mem://features/writing-styles-by-publish-type.md.
 */
export const approveBriefPlan = async (
  workspaceId: string,
  pieceId: string,
  approvedBrief: Record<string, unknown>,
) => {
  const approvedBriefWithPinnedDocs = await mergePersistedCorpusIds(workspaceId, pieceId, approvedBrief);
  const publishTypeKey = typeof approvedBriefWithPinnedDocs.publish_type_key === "string"
    ? approvedBriefWithPinnedDocs.publish_type_key.trim()
    : "";
  if (!publishTypeKey) {
    throw new Error(
      "The planning agent must resolve a publish type before you can approve this brief. Ask the agent for a publish type, then try again.",
    );
  }

  const audienceAvatarId = typeof approvedBriefWithPinnedDocs.audience_avatar_id === "string"
    && approvedBriefWithPinnedDocs.audience_avatar_id.length > 0
    ? approvedBriefWithPinnedDocs.audience_avatar_id
    : null;

  const existingMetadata = await loadBriefMetadata(workspaceId, pieceId);
  const nextMetadata: Record<string, Json | null> = {
    ...existingMetadata,
    selected_avatar_id: audienceAvatarId,
  };

  const { data, error } = await supabase
    .from("content_pieces")
    .update({
      approved_brief_json: approvedBriefWithPinnedDocs as Json,
      content_type: publishTypeKey,
      metadata: nextMetadata as Json,
      status: "approved",
    })
    .eq("workspace_id", workspaceId)
    .eq("id", pieceId)
    .select("*")
    .single();
  if (error) throw new Error(getFriendlyMessage(error, "Failed to approve the brief"));

  await syncApprovedPlanSnapshots(workspaceId, pieceId, data.updated_at ?? null);

  return data as ContentBrief;
};

export const clearApprovedBriefPlan = async (workspaceId: string, pieceId: string) => {
  const { data: existingPiece, error: existingError } = await supabase
    .from("content_pieces")
    .select("active_brief_id")
    .eq("workspace_id", workspaceId)
    .eq("id", pieceId)
    .single();
  if (existingError) throw new Error(getFriendlyMessage(existingError, "Failed to load the brief"));

  const { data, error } = await supabase
    .from("content_pieces")
    .update({ approved_brief_json: null, status: "draft", active_brief_id: null })
    .eq("workspace_id", workspaceId)
    .eq("id", pieceId)
    .select("*")
    .single();
  if (error) throw new Error(getFriendlyMessage(error, "Failed to clear the approved brief"));

  if (existingPiece?.active_brief_id) {
    const { error: clearBriefRowError } = await supabase
      .from("content_briefs")
      .update({
        intake_attachments: [] as unknown as Json,
        structured_sections: [] as unknown as Json,
        source_fidelity_contract: {} as Json,
        voice_contract: {} as Json,
      })
      .eq("workspace_id", workspaceId)
      .eq("id", existingPiece.active_brief_id);
    if (clearBriefRowError) {
      throw new Error(getFriendlyMessage(clearBriefRowError, "Failed to clear stale brief attachments"));
    }
  }

  return data as ContentBrief;
};

